import { useMemo, useRef, useState } from 'react';
import type { GameId, GameSection as Section } from '@tg/shared';
import { Link } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocale } from '@/i18n/useLocale';
import { useFormat } from '@/i18n/useFormat';
import { formatCountdown } from '@/i18n/format';
import { paths } from '@/app/paths';
import { cx, useIsMobile } from '@/ui';
import { VersionLine } from '@/features/version/VersionLine';
import { GuestDemos } from './GuestDemos';
import { AccountButton, DailyButton, LangSwitch, TabBar, WalletOrSignIn } from '@/shell/parts';
import { MobileHeader, PrivacyLink } from '@/shell/Shells';
import { useLobbyLive, useNow, winsToTicker, type LobbyLiveState } from './useLobbyLive';
import { flightAt, type FlightState } from './LiftoffCurve';
import {
  DASH,
  LeftNav,
  LiftoffFeed,
  LiftoffHero,
  LiftoffChart,
  MACHINES,
  REEL_SLOTS,
  CELL_BG,
  GameCard,
  GameSection,
  PotBankTable,
  SceneBg,
  hongbaoTables,
  mahjongQueues,
  doudizhuQueues,
  type DdzLiveQueue,
  type HbLiveTable,
  type MjtLiveQueue,
  PotCard,
  RouletteCard,
  SOLO_ORIGINALS,
  TABLE_ORIGINALS,
  PUZZLES,
  STRATEGY,
  type LiveData,
  type TableOriginal,
  Ticker,
  crashCounters,
  potFill,
  potGradient,
  rouletteShares,
  useGameName,
  useSearchName,
  useRouletteLabel,
  type RouletteClock,
} from './parts';
import s from './lobby.module.css';
import { useSeason } from '@/features/season/useSeason';
import { SeasonBanner, SeasonGarland } from '@/features/season/SeasonDecor';
import { JackpotBanner } from '@/features/jackpot/JackpotBanner';
import { NewMark } from '@/features/passport/marks';
import { QuestCard } from '@/features/quests/QuestCard';
import { CatalogSearch, EventsBlock, FilterChips, ResumeBlock, shows, useLobbyFilter, useScrollMemory, type LobbyFilter } from './catalog';

/**
 * Lobby (design/lobby, docs/four-games-mobile-plan.md): desktop 232 | 1fr | 332, phone 390 + tab bar. Sections in
 * catalog order (@tg/shared GAME_SECTIONS) — «Originals» (live Liftoff / Pot / Roulette widgets on their scenes + solo
 * cards), «Puzzles», «Strategy», «Tables», «Slots», «Machines»: a grid of cover cards (3–4 columns on desktop, 2 on
 * the phone), narrowed by the section chips and the search.
 * Every number comes from GET /api/lobby/live (+ Liftoff phase pushes); without data the blocks are neutral ("—").
 */
export default function LobbyPage() {
  const live = useLobbyLive();
  const mobile = useIsMobile();
  const serverNow = useNow() + live.offset;
  const d = live.data;
  const flight = flightAt(d?.crash ?? null, serverNow);
  const roulette: RouletteClock = d?.roulette
    ? { phase: d.roulette.phase, leftMs: Math.max(0, d.roulette.phaseEndsAt - serverNow) }
    : null;
  const potEnds = d?.pot?.countdownEndsAt ?? null;
  const potLeft = potEnds === null ? null : Math.max(0, potEnds - serverNow);
  const hbTables = hongbaoTables(d ?? null, serverNow);
  const mjtQueues = mahjongQueues(d ?? null);
  const ddzQueues = doudizhuQueues(d ?? null);
  const props: ViewProps = { live, flight, roulette, potLeft, hbTables, mjtQueues, ddzQueues };
  return mobile ? <MobileLobby {...props} /> : <DesktopLobby {...props} />;
}

interface ViewProps {
  live: LobbyLiveState;
  flight: FlightState;
  roulette: RouletteClock;
  potLeft: number | null;
  /** Red Envelopes tables; null — no live data (static card) */
  hbTables: HbLiveTable[] | null;
  /** Mahjong Tables queues per stake; null — no live data (static card) */
  mjtQueues: MjtLiveQueue[] | null;
  /** Dou Dizhu queues per tier; null — no live data (static card) */
  ddzQueues: DdzLiveQueue[] | null;
}

/** Live widgets of a table original by game (Red Envelopes seats, Mahjong Tables / Dou Dizhu queues). */
function tableLive(g: TableOriginal, p: Pick<ViewProps, 'hbTables' | 'mjtQueues' | 'ddzQueues'>) {
  if (g === 'hongbao') return { tables: p.hbTables };
  return { queues: g === 'mahjong-table' ? p.mjtQueues : p.ddzQueues };
}

/**
 * What the lobby lists for a search text and a section filter: the games of every section that match the text
 * (localized name + aliases), the live widgets of Liftoff / Pot / Roulette when one of them matches. A search looks
 * through every section whatever the chip (the chips narrow browsing, the search finds).
 */
function useCatalog(q: string, filter: LobbyFilter) {
  const name = useGameName();
  const searchName = useSearchName();
  const needle = q.trim().toLocaleLowerCase();
  const match = (n: string) => !needle || n.toLocaleLowerCase().includes(needle);
  const f: LobbyFilter = needle ? 'all' : filter;
  const pick = <T extends GameId>(section: Section, list: readonly T[], by: (g: T) => string) =>
    shows(f, section) ? list.filter((g) => match(by(g))) : [];
  const showLive = shows(f, 'originals') && (match(name('crash')) || match(name('pot')) || match(name('roulette')));
  const solo = pick('originals', SOLO_ORIGINALS, name);
  const puzzles = pick('puzzles', PUZZLES, searchName);
  const strategy = pick('strategy', STRATEGY, searchName);
  const tables = pick('tables', TABLE_ORIGINALS, searchName);
  const slots = pick('slots', REEL_SLOTS, name);
  const machines = pick('machines', MACHINES, name);
  const nothing = !showLive && solo.length + puzzles.length + strategy.length + tables.length + slots.length + machines.length === 0;
  return { showLive, solo, puzzles, strategy, tables, slots, machines, nothing };
}

type Catalog = ReturnType<typeof useCatalog>;

/** The card sections after «Originals» in catalog order (Puzzles, Strategy, Tables, Slots, Machines). */
function OtherSections({ c, d, compact, hbTables, mjtQueues, ddzQueues }: { c: Catalog; d: LiveData; compact?: boolean } & Pick<ViewProps, 'hbTables' | 'mjtQueues' | 'ddzQueues'>) {
  return (
    <>
      {c.puzzles.length > 0 && (
        <GameSection titleId="lobby.puzzles">
          {c.puzzles.map((g) => (
            <GameCard key={g} game={g} compact={compact} />
          ))}
        </GameSection>
      )}
      {c.strategy.length > 0 && (
        <GameSection titleId="lobby.strategy">
          {c.strategy.map((g) => (
            <GameCard key={g} game={g} compact={compact} />
          ))}
        </GameSection>
      )}
      {/* Red Envelopes, Mahjong Tables and Dou Dizhu: shared tables with live seats / queues */}
      {c.tables.length > 0 && (
        <GameSection titleId="lobby.tablesCap">
          {c.tables.map((g) => (
            <GameCard key={g} game={g} compact={compact} {...tableLive(g, { hbTables, mjtQueues, ddzQueues })} />
          ))}
        </GameSection>
      )}
      {c.slots.length > 0 && (
        <GameSection titleId="lobby.slots" icon="slots">
          {c.slots.map((g) => (
            <GameCard key={g} game={g} data={d} compact={compact} />
          ))}
        </GameSection>
      )}
      {/* lever machines (one-armed bandits): their own section after the slots */}
      {c.machines.length > 0 && (
        <GameSection titleId="lobby.machines" icon="machines">
          {c.machines.map((g) => (
            <GameCard key={g} game={g} data={d} compact={compact} />
          ))}
        </GameSection>
      )}
      {c.nothing && (
        <div className={s.empty} role="status">
          <FormattedMessage id="lobby.noResults" />
        </div>
      )}
    </>
  );
}

function DesktopLobby({ live, flight, roulette, potLeft, hbTables, mjtQueues, ddzQueues }: ViewProps) {
  const intl = useIntl();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useLobbyFilter();
  const c = useCatalog(q, filter);
  const filterNav = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase();
    return (n: string) => !needle || n.toLocaleLowerCase().includes(needle);
  }, [q]);
  const d = live.data;
  const season = useSeason();

  return (
    <div className={s.root}>
      <LeftNav data={d} flight={flight} roulette={roulette} filter={filterNav} />
      <div className={s.main}>
        <div className={s.top}>
          <label className={s.search}>
            <span aria-hidden="true">/</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={intl.formatMessage({ id: 'lobby.search' })}
              aria-label={intl.formatMessage({ id: 'lobby.search' })}
            />
          </label>
          {season && <SeasonGarland compact />}
          <LangSwitch />
          <WalletOrSignIn />
          <AccountButton />
        </div>
        <Ticker items={d ? winsToTicker(d.wins) : null} />
        <main className={s.body}>
          <FilterChips value={filter} onChange={setFilter} className={s.filtersDesk} />
          {!q.trim() && filter === 'all' && <GuestDemos />}
          <JackpotBanner />
          {season && <SeasonBanner season={season} />}
          {(c.showLive || c.solo.length > 0) && (
            <GameSection
              titleId="lobby.originals"
              lead={
                c.showLive && (
                  <div className={s.topRow}>
                    <LiftoffHero data={d} offset={live.offset} stale={live.crashCountersStale} />
                    <div className={s.sideCol}>
                      <PotCard data={d} potLeft={potLeft} />
                      <RouletteCard data={d} clock={roulette} />
                    </div>
                  </div>
                )
              }
            >
              {c.solo.map((g) => (
                <GameCard key={g} game={g} />
              ))}
            </GameSection>
          )}
          <OtherSections c={c} d={d} hbTables={hbTables} mjtQueues={mjtQueues} ddzQueues={ddzQueues} />
        </main>
        <footer className={cx(s.footer, s.footerRow)}>
          <PrivacyLink />
          <VersionLine />
        </footer>
      </div>
      <aside className={s.rail}>
        <div className={s.railTop}>
          <DailyButton block />
        </div>
        <QuestCard />
        <PotBankTable data={d} />
        <LiftoffFeed data={d} />
      </aside>
    </div>
  );
}

/**
 * Phone catalog: search and section chips, «Continue», one folded events block, then the sections — Originals keep
 * the live Liftoff / Pot / Roulette previews (each one whole link), every other section is a grid of equal cards.
 * The filter and the scroll position survive a visit of a game (catalog.tsx).
 */
function MobileLobby({ live, potLeft, roulette, hbTables, mjtQueues, ddzQueues }: ViewProps) {
  const l = useLocale();
  const f = useFormat();
  const intl = useIntl();
  const name = useGameName();
  const d = live.data;
  const { roundNo, counters } = crashCounters(d, live.crashCountersStale);
  const rouletteLabel = useRouletteLabel();
  const share = rouletteShares(d);
  const pct = (c: 'red' | 'black' | 'green') => (share ? f.pct(share[c], 0) : DASH);
  const short = (c: 'red' | 'black' | 'green') => intl.formatMessage({ id: `lobby.roulette.short.${c}` });
  const last = Array.from({ length: 5 }, (_, i) => d?.roulette?.last[i] ?? null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useLobbyFilter();
  const c = useCatalog(q, filter);
  const body = useRef<HTMLElement | null>(null);
  useScrollMemory(body);
  const searching = q.trim() !== '';
  return (
    <div className={s.mRoot}>
      <MobileHeader />
      <Ticker items={d ? winsToTicker(d.wins) : null} mobile />
      <main className={s.mBody} ref={body}>
        <div className={s.mCatalogTop}>
          <CatalogSearch value={q} onChange={setQ} />
          <FilterChips value={filter} onChange={setFilter} />
        </div>
        {!searching && <ResumeBlock />}
        {!searching && filter === 'all' && <GuestDemos />}
        {!searching && <EventsBlock />}
        {(c.showLive || c.solo.length > 0) && (
          <GameSection
            titleId="lobby.originals"
            lead={
              c.showLive && (
                <>
                  <Link to={paths.game(l, 'crash')} className={s.mHero}>
                    <SceneBg game="crash" width={360} height={200} />
                    <div className={s.mHeroHead}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>Liftoff</span>
                      <NewMark game="crash" />
                      <span className={s.monoDim} style={{ fontSize: 10 }}>
                        {roundNo}
                      </span>
                      <span className={s.spacer} />
                      <span className={s.monoDim} style={{ fontSize: 10, color: 'var(--teal-text)' }}>
                        ● {counters ? f.int(counters.players) : DASH}
                      </span>
                    </div>
                    <LiftoffChart data={d} offset={live.offset} compact />
                    <div className={s.mHeroFoot}>
                      <span>
                        <FormattedMessage id="lobby.bets" /> <b>{counters ? f.int(counters.bets) : DASH}</b>
                      </span>
                      <span className={s.spacer} />
                      <span className={s.play}>
                        <FormattedMessage id="lobby.play" />
                      </span>
                    </div>
                  </Link>
                  <div className={s.mLivePair}>
                    <Link to={paths.game(l, 'pot')} className={s.mLive}>
                      <SceneBg game="pot" width={180} height={120} />
                      <span className={s.mRowTitle}>
                        {name('pot')} <NewMark game="pot" />
                      </span>
                      <div className={s.mPotRow}>
                        <div
                          className={s.mDonut}
                          style={{
                            background: potGradient(d?.pot?.top.map((p) => p.stake) ?? [], d?.pot?.bank ?? 0, 0),
                          }}
                          aria-hidden="true"
                        >
                          <span />
                        </div>
                        <span className="mono" style={{ fontSize: 16, color: 'var(--orange-text)' }}>
                          {d?.pot ? f.int(d.pot.bank) : DASH}
                        </span>
                      </div>
                      <span className={s.monoMuted} style={{ textTransform: 'uppercase' }}>
                        {potFill(d)}
                        {potLeft !== null && (
                          <>
                            {' · '}
                            <span className={cx(s.countdown, s.onScene)}>
                              <FormattedMessage id="lobby.start" /> {formatCountdown(potLeft)}
                            </span>
                          </>
                        )}
                      </span>
                    </Link>
                    <Link to={paths.game(l, 'roulette')} className={s.mLive}>
                      <SceneBg game="roulette" width={180} height={120} />
                      <div className={s.mLiveHead}>
                        <span className={s.mRowTitle}>
                          {name('roulette')} <NewMark game="roulette" />
                        </span>
                        <span className={cx('mono', s.countdown, s.onScene)} style={{ fontSize: 12 }}>
                          {rouletteLabel(roulette)}
                        </span>
                      </div>
                      <div className={s.mStrip} aria-hidden="true">
                        {last.map((cell, i) => (
                          <span
                            key={i}
                            className={s.stripCell}
                            style={{ backgroundColor: cell ? CELL_BG[cell] : 'var(--border-2)' }}
                          />
                        ))}
                      </div>
                      <span className={s.monoMuted}>
                        {short('red')} {pct('red')} · {short('black')} {pct('black')} · {short('green')}{' '}
                        {pct('green')}
                      </span>
                    </Link>
                  </div>
                </>
              )
            }
          >
            {c.solo.map((g) => (
              <GameCard key={g} game={g} compact />
            ))}
          </GameSection>
        )}
        <OtherSections c={c} d={d} compact hbTables={hbTables} mjtQueues={mjtQueues} ddzQueues={ddzQueues} />
        <div className={s.mDisc}><PrivacyLink /></div>
        <VersionLine className={s.mVersion} />
      </main>
      <TabBar />
    </div>
  );
}
